/**
 * "My scheduled tasks" (#2303): the viewer's own tasks (#2281), each with its
 * status (waiting, running, disabled) and how its last run ended (completed,
 * skipped, failed, #2207), newest updated first. A row or card opens the
 * task (`/tasks/:id`); "See all" is the Tasks tab of the viewer's profile.
 *
 * One request for the section, its `count` tasks with their last runs
 * (`searchProfileTasks(me, { withLastRun })`): the server looks up the page's
 * last runs at once, for the caller's own tasks only. It loads once the
 * section comes near the viewport (`useNearLoad`); a hidden section is not
 * drawn and asks nothing.
 */
import { useCallback } from 'react';
import { Link } from 'react-router';
import { faCalendarClock } from '@fortawesome/pro-solid-svg-icons';
import { searchProfileTasks } from '@/api/tasks';
import type { DiscoverLayout, DiscoverSection } from '@/api/discoverPreferences';
import type { Task } from '@/types';
import MarkedText from '@/components/common/marking/MarkedText';
import { getLabel } from '@/utils/label';
import { relativeTimeVerbose } from '@/utils/relativeTime';
import { SectionHead, SectionMessage, SectionSkeleton } from '../DiscoverCards';
import { countOf, variantOf } from '../saved/discoverLayout';
import { outcomeChip, statusChip, type Chip } from './taskChips';
import { useNearLoad } from './useNearLoad';
import './scheduledTasks.css';

function ChipView({ chip, title }: Readonly<{ chip: Chip; title?: string }>) {
  return (
    <span className={`dst-chip dst-chip--${chip.tone}`} title={title}>
      <span className="dst-chip-dot" aria-hidden="true"/>
      {chip.label}
    </span>
  );
}

/** The last run's chip and when it was, e.g. "Failed · 2 hours ago". */
function LastRun({ task }: Readonly<{ task: Task }>) {
  const at = task.$lastRun?.at;
  const when = at ? new Date(at) : null;
  return (
    <span className="dst-lastrun">
      <span className="sr-only">Last run: </span>
      <ChipView chip={outcomeChip(task)}/>
      {when && !Number.isNaN(when.getTime()) && (
        <time dateTime={at} title={when.toLocaleString()} className="dst-when">{relativeTimeVerbose(when)}</time>
      )}
    </span>
  );
}

function modelNameOf(task: Task): string {
  return getLabel(task.$modelName) || '';
}

function TaskRow({ task, index }: Readonly<{ task: Task; index: number }>) {
  const label = getLabel(task.metadata?.label) || 'Untitled task';
  const model = modelNameOf(task);
  return (
    <Link to={`/tasks/${task._id}`} className="edit-row dst-row" style={{ animationDelay: `${(index % 12) * 25}ms` }}>
      <span className="edit-iconpill" aria-hidden="true">{label.charAt(0).toUpperCase()}</span>
      <div className="min-w-0">
        <div className="dst-name" title={label}>
          <MarkedText label={task.metadata?.label} fallback="Untitled task" truncate/>
        </div>
        {model && (
          <div className="dst-sub" title={model}>
            <MarkedText label={task.$modelName} truncate/>
          </div>
        )}
      </div>
      <span className="dst-chips">
        <ChipView chip={statusChip(task)} title="Status"/>
        <LastRun task={task}/>
      </span>
    </Link>
  );
}

function TaskCard({ task, index }: Readonly<{ task: Task; index: number }>) {
  const model = modelNameOf(task);
  return (
    <Link to={`/tasks/${task._id}`} className="edit-pick dst-card" style={{ animationDelay: `${(index % 12) * 30}ms` }}>
      <h4 className="dst-card-name"><MarkedText label={task.metadata?.label} fallback="Untitled task"/></h4>
      {model && <p className="dst-sub"><MarkedText label={task.$modelName}/></p>}
      <div className="dst-card-chips">
        <ChipView chip={statusChip(task)} title="Status"/>
        <LastRun task={task}/>
      </div>
    </Link>
  );
}

export interface ScheduledTasksSectionProps {
  section: DiscoverSection;
  layout?: DiscoverLayout;
  /** The viewer's profile id: whose tasks. None yet, nothing is asked. */
  profileId: string | undefined;
  className?: string;
}

export default function ScheduledTasksSection({ section, layout, profileId, className = 'mb-9' }: Readonly<ScheduledTasksSectionProps>) {
  const variant = variantOf(section, layout);
  const count = countOf(section, layout);
  const load = useCallback(
    () => searchProfileTasks(profileId!, { limit: count, scope: 'mine', withLastRun: true }).then((r) => r.data ?? []),
    [profileId, count],
  );
  const { ref, status, value, retry } = useNearLoad<Task[]>(profileId ? load : null);
  const tasks = value ?? [];

  const renderBody = () => {
    if (status === 'loading') return <SectionSkeleton variant={variant === 'grid' ? 'grid' : 'list'}/>;
    if (status === 'error') {
      return <SectionMessage onRetry={retry}>Couldn't load your scheduled tasks.</SectionMessage>;
    }
    if (tasks.length === 0) {
      return (
        <SectionMessage>
          You have no scheduled tasks. To schedule one, open a model in Build and choose Automation.
        </SectionMessage>
      );
    }
    if (variant === 'grid') {
      return (
        <div className="edit-picks-grid">
          {tasks.map((t, i) => <TaskCard key={t._id} task={t} index={i}/>)}
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-1.5">
        {tasks.map((t, i) => <TaskRow key={t._id} task={t} index={i}/>)}
      </div>
    );
  };

  return (
    <section ref={ref} className={className} aria-label="My scheduled tasks" aria-busy={status === 'loading'} data-section={section.id}>
      <SectionHead
        icon={faCalendarClock}
        kicker="Your scheduled tasks"
        title="My scheduled tasks"
        actions={profileId && (
          <Link className="edit-section-link" to={`/users/profile/${profileId}/tasks?show=mine`}>See all</Link>
        )}
      />
      {renderBody()}
    </section>
  );
}
